// src/redux/stockGroups/stockGroupSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchGroupsForStock,
  fetchRecommendedGroupsForStock,
  fetchStocksForGroup,
  associateStocksWithGroup,
  fetchPostsForStock,
} from './groupsToStockActions';

const ensureTicker = (state, ticker) => {
  if (!state.groupsByTicker[ticker]) {
    state.groupsByTicker[ticker] = { items: [], status: 'idle', error: null, lastFetched: null };
  }
  if (!state.recommendedByTicker[ticker]) {
    state.recommendedByTicker[ticker] = { items: [], stock: null, status: 'idle', error: null, lastFetched: null };
  }
  if (!state.postsByTicker[ticker]) {
    state.postsByTicker[ticker] = {
      items: [],
      hasMore: true,
      status: 'idle',
      error: null,
      lastFetched: null,
      lastQuery: { skip: 0, limit: 10, sortBy: 'created_at' },
    };
  }
};

const initialState = {
  // Groups keyed by ticker: { [TICKER]: { items:[], status, error, lastFetched } }
  groupsByTicker: {},

  // Follow-graph-ranked recommendations keyed by ticker, same shape as
  // groupsByTicker but a separate bucket since it's a distinct query
  // (personalized, auth-required) from the plain search above.
  recommendedByTicker: {},

  // Stocks keyed by groupId: { [groupId]: { items:[], status, error, lastFetched } }
  stocksByGroupId: {},

  // Posts keyed by ticker with pagination
  postsByTicker: {},

  // Association operation (global)
  associateStatus: 'idle',
  associateError: null,
};

const stockGroupSlice = createSlice({
  name: 'stockGroups',
  initialState,
  reducers: {
    clearGroupsForTicker: (state, action) => {
      const t = action.payload?.toUpperCase?.() ?? action.payload;
      if (t) {
        state.groupsByTicker[t] = { items: [], status: 'idle', error: null, lastFetched: null };
      }
    },
    clearStocksForGroup: (state, action) => {
      const gid = action.payload;
      if (gid != null) {
        state.stocksByGroupId[gid] = { items: [], status: 'idle', error: null, lastFetched: null };
      }
    },
    clearPostsForTicker: (state, action) => {
      const t = action.payload?.toUpperCase?.() ?? action.payload;
      if (t) {
        state.postsByTicker[t] = {
          items: [],
          hasMore: true,
          status: 'idle',
          error: null,
          lastFetched: null,
          lastQuery: { skip: 0, limit: 10, sortBy: 'created_at' },
        };
      }
    },
    resetAssocStatus: (state) => {
      state.associateStatus = 'idle';
      state.associateError = null;
    },
  },
  extraReducers: (builder) => {
    // ===== Groups for Stock =====
    builder
      .addCase(fetchGroupsForStock.pending, (state, action) => {
        const t = action.meta.arg;
        ensureTicker(state, t);
        state.groupsByTicker[t].status = 'loading';
        state.groupsByTicker[t].error = null;
      })
      .addCase(fetchGroupsForStock.fulfilled, (state, action) => {
        const { ticker, data } = action.payload || {};
        ensureTicker(state, ticker);
        state.groupsByTicker[ticker].status = 'succeeded';

        const arr =
          Array.isArray(data?.groups) ? data.groups :
          Array.isArray(data?.items) ? data.items :
          Array.isArray(data) ? data : [];

        state.groupsByTicker[ticker].items = arr;
        state.groupsByTicker[ticker].lastFetched = Date.now();
      })
      .addCase(fetchGroupsForStock.rejected, (state, action) => {
        const { ticker, message } = action.payload || {};
        if (ticker) ensureTicker(state, ticker);
        if (ticker) {
          state.groupsByTicker[ticker].status = 'failed';
          state.groupsByTicker[ticker].error = message || action.error?.message || 'Failed to fetch groups for stock';
        }
      });

    // ===== Recommended Groups for Stock =====
    builder
      .addCase(fetchRecommendedGroupsForStock.pending, (state, action) => {
        const t = action.meta.arg;
        ensureTicker(state, t);
        state.recommendedByTicker[t].status = 'loading';
        state.recommendedByTicker[t].error = null;
      })
      .addCase(fetchRecommendedGroupsForStock.fulfilled, (state, action) => {
        const { ticker, data } = action.payload || {};
        ensureTicker(state, ticker);
        state.recommendedByTicker[ticker].status = 'succeeded';
        state.recommendedByTicker[ticker].items = Array.isArray(data?.groups) ? data.groups : [];
        state.recommendedByTicker[ticker].stock = data?.stock ?? null;
        state.recommendedByTicker[ticker].lastFetched = Date.now();
      })
      .addCase(fetchRecommendedGroupsForStock.rejected, (state, action) => {
        const { ticker, message } = action.payload || {};
        if (ticker) ensureTicker(state, ticker);
        if (ticker) {
          state.recommendedByTicker[ticker].status = 'failed';
          state.recommendedByTicker[ticker].error = message || action.error?.message || 'Failed to fetch recommended groups for stock';
        }
      });

    // ===== Stocks for Group =====
    builder
      .addCase(fetchStocksForGroup.pending, (state, action) => {
        const gid = action.meta.arg;
        if (!state.stocksByGroupId[gid]) {
          state.stocksByGroupId[gid] = { items: [], status: 'idle', error: null, lastFetched: null };
        }
        state.stocksByGroupId[gid].status = 'loading';
        state.stocksByGroupId[gid].error = null;
      })
      .addCase(fetchStocksForGroup.fulfilled, (state, action) => {
        const { groupId, data } = action.payload || {};
        if (!state.stocksByGroupId[groupId]) {
          state.stocksByGroupId[groupId] = { items: [], status: 'idle', error: null, lastFetched: null };
        }
        state.stocksByGroupId[groupId].status = 'succeeded';

        const arr =
          Array.isArray(data?.stocks) ? data.stocks :
          Array.isArray(data?.items) ? data.items :
          Array.isArray(data) ? data : [];

        state.stocksByGroupId[groupId].items = arr;
        state.stocksByGroupId[groupId].lastFetched = Date.now();
      })
      .addCase(fetchStocksForGroup.rejected, (state, action) => {
        const { groupId, message } = action.payload || {};
        if (!state.stocksByGroupId[groupId]) {
          state.stocksByGroupId[groupId] = { items: [], status: 'idle', error: null, lastFetched: null };
        }
        state.stocksByGroupId[groupId].status = 'failed';
        state.stocksByGroupId[groupId].error = message || action.error?.message || 'Failed to fetch stocks for group';
      });

    // ===== Associate Stocks with Group =====
    builder
      .addCase(associateStocksWithGroup.pending, (state) => {
        state.associateStatus = 'loading';
        state.associateError = null;
      })
      .addCase(associateStocksWithGroup.fulfilled, (state, action) => {
        state.associateStatus = 'succeeded';
        const { groupId, data } = action.payload || {};
        // If backend returns updated stock list, use it
        if (groupId != null && data) {
          const arr =
            Array.isArray(data?.stocks) ? data.stocks :
            Array.isArray(data?.items) ? data.items :
            Array.isArray(data) ? data : null;

          if (arr) {
            if (!state.stocksByGroupId[groupId]) {
              state.stocksByGroupId[groupId] = { items: [], status: 'idle', error: null, lastFetched: null };
            }
            state.stocksByGroupId[groupId].items = arr;
            state.stocksByGroupId[groupId].lastFetched = Date.now();
          }
        }
      })
      .addCase(associateStocksWithGroup.rejected, (state, action) => {
        state.associateStatus = 'failed';
        state.associateError = action.payload?.message || action.error?.message || 'Failed to associate stocks with group';
      });

    // ===== Posts for Stock (paginated) =====
    builder
      .addCase(fetchPostsForStock.pending, (state, action) => {
        const { ticker } = action.meta.arg;
        ensureTicker(state, ticker);
        state.postsByTicker[ticker].status = 'loading';
        state.postsByTicker[ticker].error = null;
      })
      .addCase(fetchPostsForStock.fulfilled, (state, action) => {
        const { ticker, skip, limit, sortBy, data } = action.payload || {};
        ensureTicker(state, ticker);
        const bucket = state.postsByTicker[ticker];

        bucket.status = 'succeeded';
        bucket.lastFetched = Date.now();
        bucket.lastQuery = { skip, limit, sortBy };

        const posts = Array.isArray(data?.posts) ? data.posts : (Array.isArray(data) ? data : []);
        const hasMore = typeof data?.has_more === 'boolean' ? data.has_more : (posts.length === limit);

        if (!Array.isArray(bucket.items)) bucket.items = [];

        if (skip === 0) {
          bucket.items = posts;
        } else {
          const existingIds = new Set(bucket.items.map(p => p.id));
          const merged = [...bucket.items];
          for (const p of posts) {
            if (!existingIds.has(p.id)) merged.push(p);
          }
          bucket.items = merged;
        }

        bucket.hasMore = hasMore;
      })
      .addCase(fetchPostsForStock.rejected, (state, action) => {
        const { ticker, message } = action.payload || {};
        if (ticker) ensureTicker(state, ticker);
        if (ticker) {
          state.postsByTicker[ticker].status = 'failed';
          state.postsByTicker[ticker].error = message || action.error?.message || 'Failed to fetch posts for stock';
        }
      });
  },
});

export const {
  clearGroupsForTicker,
  clearStocksForGroup,
  clearPostsForTicker,
  resetAssocStatus,
} = stockGroupSlice.actions;

export default stockGroupSlice.reducer;

/** Selectors */
export const selectGroupsForTicker = (state, ticker) =>
  state.stockGroups.groupsByTicker[ticker?.toUpperCase?.() ?? ticker]?.items ?? [];

export const selectGroupsForTickerStatus = (state, ticker) =>
  state.stockGroups.groupsByTicker[ticker?.toUpperCase?.() ?? ticker]?.status ?? 'idle';

export const selectRecommendedGroupsForTicker = (state, ticker) =>
  state.stockGroups.recommendedByTicker[ticker?.toUpperCase?.() ?? ticker]?.items ?? [];

export const selectRecommendedGroupsForTickerStatus = (state, ticker) =>
  state.stockGroups.recommendedByTicker[ticker?.toUpperCase?.() ?? ticker]?.status ?? 'idle';

export const selectRecommendedStockForTicker = (state, ticker) =>
  state.stockGroups.recommendedByTicker[ticker?.toUpperCase?.() ?? ticker]?.stock ?? null;

export const selectStocksForGroup = (state, groupId) =>
  state.stockGroups.stocksByGroupId[groupId]?.items ?? [];

export const selectStocksForGroupStatus = (state, groupId) =>
  state.stockGroups.stocksByGroupId[groupId]?.status ?? 'idle';

export const selectPostsForTicker = (state, ticker) =>
  state.stockGroups.postsByTicker[ticker?.toUpperCase?.() ?? ticker]?.items ?? [];

export const selectPostsForTickerStatus = (state, ticker) =>
  state.stockGroups.postsByTicker[ticker?.toUpperCase?.() ?? ticker]?.status ?? 'idle';

export const selectHasMorePostsForTicker = (state, ticker) =>
  state.stockGroups.postsByTicker[ticker?.toUpperCase?.() ?? ticker]?.hasMore ?? true;

export const selectAssociateStatus = (state) => state.stockGroups.associateStatus;
export const selectAssociateError = (state) => state.stockGroups.associateError;
